import { Link } from "react-router-dom";
import { receptionistContent } from "../content/receptionist.js";

function IconPhone() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.5 3h3l1.5 5-2 1.2a11 11 0 0 0 5.3 5.3L15.5 13l5 1.5v3a2 2 0 0 1-2 1.7A16 16 0 0 1 3 8.5 2 2 0 0 1 4.7 6.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconVoice() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M6 11v1a6 6 0 0 0 12 0v-1M12 18v3M8 21h8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconDashboard() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="8" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13" y="4" width="8" height="4" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13" y="10" width="8" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3" y="13" width="8" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function IconDeploy() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5v-9Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12 12v9M4 7.5 12 12l8-4.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

const FEATURE_ICONS = {
  phone: IconPhone,
  voice: IconVoice,
  dashboard: IconDashboard,
  deploy: IconDeploy,
};

export default function ReceptionistPage() {
  const c = receptionistContent;

  return (
    <article className="site-page receptionist-page">
      <div className="site-page__inner site-page__inner--wide receptionist-page__inner">
        <header className="receptionist-hero">
          <p className="site-page__eyebrow">{c.eyebrow}</p>
          <h1 className="site-page__title receptionist-hero__title">{c.title}</h1>
          <p className="site-page__lead receptionist-hero__lead">{c.lead}</p>
          <ul className="receptionist-hero__points">
            {c.heroPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="site-page__cta receptionist-hero__cta">
            <Link to="/contact" className="site-button-primary">
              {c.cta.primary}
            </Link>
            <Link to="/pricing" className="site-button-ghost">
              {c.cta.secondary}
            </Link>
          </p>
        </header>

        <section className="receptionist-features" aria-labelledby="receptionist-features-heading">
          <h2 id="receptionist-features-heading" className="receptionist-section-title">
            Built for real businesses
          </h2>
          <div className="receptionist-features__grid">
            {c.features.map((f) => {
              const Icon = FEATURE_ICONS[f.icon] ?? IconPhone;
              return (
                <article key={f.id} className="receptionist-feature-card">
                  <span className="receptionist-feature-card__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="receptionist-desktop" aria-labelledby="receptionist-desktop-heading">
          <h2 id="receptionist-desktop-heading" className="receptionist-section-title">
            {c.desktopTieIn.title}
          </h2>
          <p className="receptionist-desktop__body">{c.desktopTieIn.body}</p>
          <div className="receptionist-flow" aria-hidden="true">
            <div className="receptionist-flow__node">
              <span className="receptionist-flow__label">Your desk</span>
              <span className="receptionist-flow__hint">Natural voice assistant</span>
            </div>
            <span className="receptionist-flow__arrow" />
            <div className="receptionist-flow__node receptionist-flow__node--core">
              <span className="receptionist-flow__label">One assistant</span>
              <span className="receptionist-flow__hint">Same answers &amp; tone</span>
            </div>
            <span className="receptionist-flow__arrow" />
            <div className="receptionist-flow__node">
              <span className="receptionist-flow__label">Your phone line</span>
              <span className="receptionist-flow__hint">Callers hear the same voice</span>
            </div>
          </div>
        </section>

        <section
          className="receptionist-dashboard-preview"
          aria-labelledby="receptionist-dashboard-heading"
        >
          <h2 id="receptionist-dashboard-heading" className="receptionist-section-title">
            {c.dashboard.title}
          </h2>
          <p className="receptionist-dashboard-preview__subtitle">{c.dashboard.subtitle}</p>

          <div className="receptionist-dash" role="img" aria-label="Sample live receptionist dashboard">
            <div className="receptionist-dash__top">
              <div>
                <p className="receptionist-dash__eyebrow">Command center</p>
                <p className="receptionist-dash__business">{c.dashboard.businessName}</p>
              </div>
              <span className="receptionist-dash__live">
                <span className="receptionist-dash__live-dot" />
                {c.dashboard.liveBadge}
              </span>
            </div>

            <div className="receptionist-dash__metrics">
              <div className="receptionist-dash__metric">
                <span className="receptionist-dash__metric-value">{c.dashboard.metrics.totalCalls}</span>
                <span className="receptionist-dash__metric-label">Calls handled</span>
              </div>
              <div className="receptionist-dash__metric receptionist-dash__metric--highlight">
                <span className="receptionist-dash__metric-value">{c.dashboard.metrics.activeNow}</span>
                <span className="receptionist-dash__metric-label">On the line now</span>
              </div>
              <div className="receptionist-dash__metric">
                <span className="receptionist-dash__metric-value">{c.dashboard.metrics.lastUpdate}</span>
                <span className="receptionist-dash__metric-label">Last update</span>
              </div>
            </div>

            <div className="receptionist-dash__panels">
              <div className="receptionist-dash__panel">
                <h3>Who we&apos;re talking to right now</h3>
                <ul className="receptionist-dash__active-list">
                  {c.dashboard.activeCalls.map((call) => (
                    <li key={call.id} className="receptionist-dash__active-item">
                      <div className="receptionist-dash__active-head">
                        <strong>{call.id}</strong>
                        <span className="receptionist-dash__pill">{call.state}</span>
                      </div>
                      <p>{call.snippet}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="receptionist-dash__panel">
                <h3>Recent calls</h3>
                <div className="receptionist-dash__table-wrap">
                  <table className="receptionist-dash__table">
                    <thead>
                      <tr>
                        <th scope="col">Call</th>
                        <th scope="col">Status</th>
                        <th scope="col">Outcome</th>
                        <th scope="col">Last said</th>
                        <th scope="col">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.dashboard.recent.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.state}</td>
                          <td>
                            <span className="receptionist-dash__outcome">{row.outcome}</span>
                          </td>
                          <td>{row.response}</td>
                          <td className="receptionist-dash__time">{row.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="receptionist-integrations" aria-labelledby="receptionist-integrations-heading">
          <h2 id="receptionist-integrations-heading" className="receptionist-section-title">
            {c.integrations.title}
          </h2>
          <p className="receptionist-integrations__body">{c.integrations.body}</p>
          <ul className="receptionist-integrations__chips" aria-label="Coming soon">
            <li>Calendars</li>
            <li>CRM</li>
            <li>Team alerts</li>
            <li>More languages</li>
          </ul>
        </section>

        <p className="site-page__cta">
          <Link to="/contact" className="site-button-primary">
            {c.cta.primary}
          </Link>
          <Link to="/" className="site-button-ghost">
            ← Back to home
          </Link>
        </p>
      </div>
    </article>
  );
}
